import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Form } from 'antd';
import dayjs from 'dayjs';
import { withPrivateRoute } from '@/components/hoc/withPrivateRoute';
import { ContainerFluid, Section } from '@/components/layouts';
import RootLayout from '@/components/layouts/RootLayout';
import {
  FieldsSkeleton,
  ProfileDetailsPanel,
  ProfileErrorState,
  ProfileLeaveTileModal,
  ProfileTour,
  MiddleCollapseButton,
  MiddleCollapsedBar,
  ProfileTiles,
  TilesSkeleton,
  dropEmptyTiles,
  useEditLanding,
  tileEditable,
  tileFieldAccess,
  useMiddleCollapsed,
  useProfileGuide,
  useProfileTileSwitch,
} from '@/components/profile';
import { Sidebar } from '@/components/sidebar';
import useEmployeeZaz from '@/components/zaz/useEmployeeZaz';
import { ROLES_PERMISSIONS, ROUTES } from '@/constants';
import { QueryParamsProvider } from '@/contexts/query-params/QueryParamsProvider';
import { useQueryParams, useToggle } from '@/hooks';
import { useEmployee } from '@/hooks/queries/use-employee';
import { cn } from '@/libs/utils';
import { API } from '@/services/api.service';
import { QUERY_KEY } from '@/shared/constants';
import {
  EMPLOYEE_EDIT_DEFAULT_PARAMS,
  isNonEmploymentTypeValue,
} from '@/shared/employee';
import { getUserFullName } from '@/utils/utils';
import EmployeeGeneralDataForm from '../_components/form/EmployeeGeneralDataForm';
import normalizeEmployeeFormData from '../_components/form/normalizeEmployeeFormData';
import {
  PROFILE_FORM_SECTIONS,
  sectionOfField,
} from '../_components/form/profileSections';
import HRDocumentsFilterForm from '../employee-edit/_components/form/HRDocumentsFilterForm';
import CompanyPreviewModal from './_components/CompanyPreviewModal';
import EmployeeDocumentsSection from './_components/EmployeeDocumentsSection';
import useEmployeePanelExtras from './_components/EmployeePanelExtras';
import useEmployeeOnboarding from './_components/useEmployeeOnboarding';
import EmployeeProfileHeader from './_components/EmployeeProfileHeader';
import EmployeeRequestSection from './_components/EmployeeRequestSection';
import OzProfileModal from './_components/OzProfileModal';
import ProfileStats from './_components/ProfileStats';
import {
  TILE_LABELS,
  buildTileGroups,
  tileSummary,
} from './_components/employeeTiles';
import useEmployeeProfileAccess from './useEmployeeProfileAccess';

// Tiles whose own tables are wide: the details panel takes the middle's place,
// so nothing has to scroll sideways.
const WIDE_PANEL_TILES = ['ugovor', 'go', 'odsustva', 'istorija', 'zarada'];

function PrivateEmployeeProfile() {
  const { t } = useTranslation(['translations', 'tables', 'forms']);
  const tp = useCallback(
    (key, opts) => t(`employeeProfile.${key}`, { ns: 'translations', ...opts }),
    [t]
  );
  const { employeeId } = useParams();
  const { queryParams, setQueryParams } = useQueryParams();

  const { data: employee, isLoading, error } = useEmployee(employeeId);
  const formData = useMemo(
    () => normalizeEmployeeFormData(employee),
    [employee]
  );

  const [form] = Form.useForm();
  // { save, discard } of the open tile, filled in by the form.
  const formActionsRef = useRef(null);
  const [filterForm] = Form.useForm();
  const {
    open: filtersOpen,
    setOpen: setFiltersOpen,
    onClose: closeFilters,
  } = useToggle(false);
  const [companyOpen, setCompanyOpen] = useState(false);
  const [ozOpen, setOzOpen] = useState(false);
  const [year, setYear] = useState(dayjs().year());

  // Old links land here with ?route=… / ?mode=edit — honour them.
  const isRequestRoute = queryParams.route === 'create-new-request';
  const [selected, setSelected] = useState(
    queryParams.route === 'hr-documents' ? 'dok' : queryParams.tile || 'licno'
  );
  const [editing, setEditing] = useState(queryParams.mode === 'edit');
  // Tiles with changes not saved yet (reported by the form).
  const [dirtySections, setDirtySections] = useState([]);
  const handleDirtySections = useCallback(
    (next) =>
      setDirtySections((prev) => (prev.join() === next.join() ? prev : next)),
    []
  );

  useEffect(() => {
    if (queryParams.route === 'hr-documents') setSelected('dok');
  }, [queryParams.route]);

  const company = employee?.company;
  const app = company?.applicationSettings || {};
  const isNonEmployment = isNonEmploymentTypeValue(
    employee?.employmentSettings?.employmentType
  );
  const hasHrFlows = !!app.isHr && !isNonEmployment;
  const isHolidayTracker =
    !!app.holidayDaysTracker &&
    new Date(app.trackerStartDate).getTime() < Date.now();

  const access = useEmployeeProfileAccess({
    employeeId,
    isHolidayTracker,
    isSuspended: employee?.status === 'SUSPENDED',
  });
  const canOpenRequest = hasHrFlows && access.canCreateRequest;

  // Non-employment workers have no HR documents or requests, and an account
  // whose request permission was taken away cannot open the form by link.
  useEffect(() => {
    if (employee && !canOpenRequest && isRequestRoute) {
      setQueryParams({ route: 'general-data' });
    }
  }, [employee, canOpenRequest, isRequestRoute, setQueryParams]);
  const isEditing = editing && access.canEdit;
  const onboarding = useEmployeeOnboarding({
    employeeId,
    employeeName: getUserFullName(employee?.firstName, employee?.lastName),
    enabled: !!employee && access.canOnboard,
    equipmentEnabled: !!employee && access.canSeeEquipment,
    isOwn: !!employee && access.canRequestOwnEquipment,
  });
  // ZAZ (request for hiring): HR of the company and the heads of the
  // employee's sector; the server refuses everyone else and the employee.
  const zaz = useEmployeeZaz({
    employeeId,
    employeeName: getUserFullName(employee?.firstName, employee?.lastName),
    enabled: !!employee && access.canSeeZaz,
  });

  const canExtendFromHere = useMemo(() => {
    if (!employee) return false;
    const { status, archiveReason, supersededBy, employmentSettings } =
      employee;
    if (status === 'ARCHIVED') {
      return archiveReason === 'LIMITED_CONTRACT_EXPIRED' && !supersededBy;
    }
    return (
      employmentSettings?.employmentType === 'limited' &&
      !!employmentSettings?.endDate &&
      dayjs(employmentSettings.endDate).isBefore(dayjs(), 'day')
    );
  }, [employee]);

  const {
    data: stats,
    isLoading: statsLoading,
    isError: statsError,
  } = useQuery({
    queryKey: [QUERY_KEY.employee, employeeId, 'profile-stats', year],
    queryFn: () => API.employees.getEmployeeProfileStats(employeeId, year),
    enabled: !!employeeId && access.canSeeRequests,
  });

  // Count for the Documents tile; same key prefix as the list, so a new
  // request or a deleted document refreshes it too.
  const { data: docsPage } = useQuery({
    queryKey: [QUERY_KEY.employeeHrDocuments, 'profile-count', employeeId],
    queryFn: () =>
      API.documents.retrieveAllDocuments({
        params: { offset: 1, limit: 1, employee: employeeId },
        from: 'employee-hr-documents',
      }),
    enabled: !!employeeId && hasHrFlows && access.canSeeRequests,
  });

  const fullName = getUserFullName(employee?.firstName, employee?.lastName);

  const renderTile = (id) => ({
    label: tp(TILE_LABELS[id]),
    dirty: isEditing && dirtySections.includes(id),
    dirtyLabel: tp('unsavedTile'),
    ...tileSummary(id, {
      employee,
      stats,
      tp,
      t,
      docsCount: docsPage?.count,
      team: extras.team,
      compensation: extras.compensation,
      equipment: onboarding.equipment,
      zaz: zaz.zaz,
    }),
  });

  const errorStatus = error?.response?.status;
  const errorMessage =
    errorStatus === 404
      ? tp('errorNotFound')
      : errorStatus === 403
        ? tp('errorForbidden')
        : tp('errorGeneric');
  const backUrl = `${ROUTES.admin}/${ROUTES.employees}${queryParams.company ? `?company=${queryParams.company}` : ''}`;

  const openRequest = () =>
    setQueryParams({
      route: 'create-new-request',
      typeOfLeaveSelect: queryParams.typeOfLeaveSelect || 'holidayLeave',
      action: undefined,
      requestId: undefined,
    });
  const closeRequest = () =>
    setQueryParams({
      route: 'general-data',
      action: undefined,
      requestId: undefined,
    });
  // Stable, so the memoized form is not re-rendered by a new function.
  const stopEditing = useCallback(() => {
    setEditing(false);
    setDirtySections([]);
  }, [setEditing]);
  const handleFinishFailed = useCallback(({ errorFields }) => {
    const first = errorFields?.[0]?.name;
    if (first) setSelected(sectionOfField(first));
  }, [setSelected]);

  // Layout guide: not over unsaved changes. It opens the tile's edit mode
  // itself to show Save, and closes it again.
  const guide = useProfileGuide({
    kind: 'employee',
    ready: !!employee && !!formData,
    blocked: isEditing && dirtySections.length > 0,
    isEditing,
    startEditing: () => setEditing(true),
    stopEditing,
  });

  const selectTileRef = useRef(null);
  const extras = useEmployeePanelExtras({
    employee,
    employeeId,
    access,
    hasHrFlows,
    canExtendFromHere,
    onOpenRequest: canOpenRequest ? openRequest : null,
    // Bound after the tile switch below, which needs the tiles first.
    onOpenTile: (id) => selectTileRef.current?.(id),
    onboarding,
    zaz,
  });

  // What the role may see and change on each tile, by the server's map.
  const fieldAccess = useMemo(
    () =>
      tileFieldAccess(
        employee?.EMPLOYEE_GENERAL_DATA_FORM_FIELDS,
        sectionOfField
      ),
    [employee]
  );
  // Tiles with content of their own besides the form (actions, lists).
  const ownContent = [
    ...new Set([...Object.keys(extras.before), ...Object.keys(extras.after)]),
  ]
    .filter((id) => extras.before[id] || extras.after[id])
    .join();

  const groups = useMemo(() => {
    if (!employee) return [];
    return dropEmptyTiles(
      buildTileGroups({
        employee,
        isHolidayTracker,
        showTeam: !!extras.team,
        showSalary: !!extras.compensation,
        showEquipment: onboarding.showEquipmentTile,
        showZaz: zaz.showTile,
      })
        // Tiles resting on a taken-away permission (restrictedPermissions).
        .map((g) => ({ ...g, tiles: g.tiles.filter(access.allowTile) }))
        .filter((g) => g.tiles.length > 0),
      fieldAccess,
      (id) => ownContent.split(',').includes(id)
    ).map((g) => ({ ...g, title: tp(g.label) }));
  }, [
    employee,
    isHolidayTracker,
    tp,
    fieldAccess,
    ownContent,
    extras.team,
    extras.compensation,
    onboarding.showEquipmentTile,
    zaz.showTile,
    access,
  ]);

  // A tile from the URL that this employee does not have, or that this role
  // has nothing to see on, falls back to the first.
  const allTiles = groups.flatMap((g) => g.tiles);
  const activeTile = allTiles.includes(selected)
    ? selected
    : allTiles[0] || 'licno';

  const canEditTile = (id) =>
    !!formData &&
    access.canEdit &&
    PROFILE_FORM_SECTIONS.includes(id) &&
    tileEditable(fieldAccess, id);

  useEditLanding({
    editing,
    setEditing,
    tiles: employee && formData ? allTiles : [],
    activeTile,
    canEditTile,
    setSelected,
  });

  const { selectTile, leavePrompt } = useProfileTileSwitch({
    isEditing,
    dirtySections,
    setSelected,
    stopEditing,
    formActionsRef,
    activeTile,
  });
  selectTileRef.current = selectTile;

  // The tile is marked at once; the panel (the whole form re-renders on a
  // tile change) follows in a background render that does not hold the click.
  const shownTile = useDeferredValue(activeTile);
  const isSwitching = shownTile !== activeTile;

  const showRequest = isRequestRoute && canOpenRequest;
  const showDocsInMiddle =
    !showRequest && shownTile === 'dok' && hasHrFlows && access.canSeeRequests;
  // The absence calendar is request data: without the permission there is no
  // middle column, and every tile gets the wide panel.
  const showWidePanel =
    !showRequest &&
    !showDocsInMiddle &&
    (WIDE_PANEL_TILES.includes(shownTile) || !access.canSeeRequests);
  // The statistics column can be folded down to a bar.
  const [middleCollapsed, setMiddleCollapsed] = useMiddleCollapsed();
  const middleFolded =
    !showRequest && !showDocsInMiddle && !showWidePanel && middleCollapsed;

  return (
    <RootLayout metadata={{ title: error ? errorMessage : fullName }}>
      <Section>
        <ContainerFluid className="foreground-wrapper !py-3">
          <div className="employee-profile flex h-[calc(100svh-104px)] min-h-[640px] flex-col overflow-hidden rounded-[10px] border-4 border-accent bg-accent">
            <EmployeeProfileHeader
              employee={employee}
              employeeId={employeeId}
              isLoading={isLoading}
              hasError={!!error}
              backUrl={backUrl}
              access={access}
              hasHrFlows={hasHrFlows}
              showTimesheet={
                (!isNonEmployment || !!app.timeTracking) &&
                access.canSeeTimesheet
              }
              canExtendFromHere={canExtendFromHere}
              setQueryParams={setQueryParams}
              onOpenDocuments={() => selectTile('dok')}
              onOpenRequest={openRequest}
              onOpenCompany={() => setCompanyOpen(true)}
              onOpenOz={() => setOzOpen(true)}
              onOpenGuide={guide.start}
              onboarding={onboarding}
              zaz={zaz}
            />

            {error ? (
              <ProfileErrorState
                message={errorMessage}
                detail={
                  errorStatus !== 404 && errorStatus !== 403
                    ? error?.response?.data?.message
                    : null
                }
                backUrl={backUrl}
                backLabel={tp('backToList')}
              />
            ) : (
              // A grid, so the details panel can move under the documents list
              // without remounting the form it holds.
              <div
                className={cn(
                  'grid min-h-0 flex-1 gap-3 p-3',
                  showRequest || showWidePanel
                    ? 'grid-cols-[380px_minmax(0,1fr)] grid-rows-[minmax(0,1fr)]'
                    : showDocsInMiddle
                      ? 'grid-cols-[380px_minmax(0,1fr)] grid-rows-[minmax(0,1fr)_auto]'
                      : middleFolded
                        ? 'grid-cols-[380px_14px_minmax(0,1fr)] grid-rows-[minmax(0,1fr)]'
                        : 'grid-cols-[380px_minmax(0,1fr)_560px] grid-rows-[minmax(0,1fr)]'
                )}
              >
                <div
                  className={cn(
                    'flex min-h-0 flex-col',
                    showDocsInMiddle && 'row-span-2'
                  )}
                >
                  {employee ? (
                    <ProfileTiles
                      label={fullName}
                      groups={groups}
                      selected={activeTile}
                      onSelect={selectTile}
                      renderTile={renderTile}
                      maxRowHeight={access.isOwnRestricted ? 96 : undefined}
                    />
                  ) : (
                    <TilesSkeleton />
                  )}
                </div>

                {showRequest ? (
                  <EmployeeRequestSection
                    employeeId={employeeId}
                    employee={employee}
                    formData={formData}
                    selectedTypeOfLeave={queryParams.typeOfLeaveSelect}
                    onClose={closeRequest}
                  />
                ) : showDocsInMiddle ? (
                  <EmployeeDocumentsSection
                    employeeId={employeeId}
                    filterForm={filterForm}
                    setQueryParams={setQueryParams}
                    onOpenFilters={setFiltersOpen}
                  />
                ) : showWidePanel ? null : middleFolded ? (
                  <MiddleCollapsedBar
                    onExpand={() => setMiddleCollapsed(false)}
                  />
                ) : (
                  <div
                      data-tour="profile-middle"
                      className="relative flex min-h-0 min-w-0 flex-col"
                    >
                    <MiddleCollapseButton
                      onCollapse={() => setMiddleCollapsed(true)}
                    />
                    <ProfileStats
                      employee={employee}
                      stats={stats}
                      statsLoading={statsLoading || isLoading}
                      statsError={statsError}
                      year={year}
                      onYearChange={setYear}
                      selected={shownTile}
                    />
                  </div>
                )}

                {/* The form stays mounted while the request panel is open, so
                    unsaved edits survive opening and closing it. */}
                <ProfileDetailsPanel
                  title={tp(TILE_LABELS[shownTile])}
                  isEditing={isEditing}
                  onEdit={
                    canEditTile(shownTile) ? () => setEditing(true) : undefined
                  }
                  dirtyLabels={dirtySections
                    .filter((id) => TILE_LABELS[id])
                    .map((id) => tp(TILE_LABELS[id]))}
                  before={extras.before[shownTile]}
                  after={extras.after[shownTile]}
                  className={cn(
                    // Documents: a compact strip under the list instead of a
                    // third column.
                    showDocsInMiddle &&
                      'employee-profile__panel--strip col-start-2 row-start-2 max-h-[240px]',
                    showWidePanel && 'employee-profile__panel--wide',
                    // Contract: the cards and the contracts table share one width.
                    shownTile === 'ugovor' && 'employee-profile__panel--full',
                    'transition-opacity',
                    isSwitching && 'opacity-60',
                    showRequest && 'hidden'
                  )}
                >
                  {formData ? (
                    <EmployeeGeneralDataForm
                      formData={formData}
                      isEdited
                      employeeId={employeeId}
                      form={form}
                      serverData={employee}
                      profileSection={shownTile}
                      readOnly={!isEditing}
                      onSaved={stopEditing}
                      onCancel={stopEditing}
                      onFinishFailed={handleFinishFailed}
                      onDirtySectionsChange={handleDirtySections}
                      actionsRef={formActionsRef}
                      className="!px-3.5 !py-3"
                    />
                  ) : (
                    <FieldsSkeleton />
                  )}
                </ProfileDetailsPanel>
              </div>
            )}
          </div>
        </ContainerFluid>
      </Section>

      <ProfileTour
        kind="employee"
        steps={guide.steps}
        current={guide.current}
        onChange={guide.change}
        onClose={guide.close}
      />

      {onboarding.modals}
      {zaz.modal}

      <ProfileLeaveTileModal
        {...leavePrompt}
        tileLabel={tp(TILE_LABELS[activeTile])}
      />

      <Sidebar
        title={t('filters', { ns: 'tables' })}
        open={filtersOpen}
        onClose={closeFilters}
      >
        <HRDocumentsFilterForm
          form={filterForm}
          queryParams={queryParams}
          setQueryParams={setQueryParams}
          onClose={closeFilters}
        />
      </Sidebar>

      {company?._id && (
        <CompanyPreviewModal
          companyId={company._id}
          open={companyOpen}
          onClose={() => setCompanyOpen(false)}
        />
      )}
      {company?._id && ozOpen && (
        <OzProfileModal
          open={ozOpen}
          onClose={() => setOzOpen(false)}
          employeeId={employeeId}
          companyId={company._id}
          fullName={fullName}
        />
      )}
    </RootLayout>
  );
}

function EmployeeProfileWithParams() {
  return (
    <QueryParamsProvider defaultParams={EMPLOYEE_EDIT_DEFAULT_PARAMS}>
      <PrivateEmployeeProfile />
    </QueryParamsProvider>
  );
}

const EmployeeProfile = withPrivateRoute(
  EmployeeProfileWithParams,
  ROLES_PERMISSIONS.EMPLOYEES.VIEW
);

export default EmployeeProfile;
